import {
  SunMedium,
  BarChart3,
  Clock,
  Package,
  AlertTriangle,
  ReceiptText,
  Home,
  Footprints,
} from 'lucide-react';

const ICONS = {
  solar_route: SunMedium,
  consumption_report: BarChart3,
  not_home_schedule: Clock,
  starter_kit: Package,
  peak_check: AlertTriangle,
  subsidy_tariff_flex: ReceiptText,
  extra_home_savings: Home,
  next_best_step: Footprints,
};

/**
 * EnergyControlFullReport — rules-driven 8-block report rendered after the preview
 * on /energy-control-report/:intakeId. No Stripe, no AI, no paywall.
 */
export default function EnergyControlFullReport({ report }) {
  if (!report || !Array.isArray(report.blocks)) return null;

  return (
    <div className="container mx-auto px-6 pb-16" data-testid="ec-full-report">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-primary/70 font-medium mb-2">
            Uw volledige plan
          </div>
          <h2
            className="text-2xl sm:text-3xl font-medium text-primary mb-3"
            style={{ fontFamily: 'Outfit' }}
            data-testid="ec-report-title"
          >
            {report.title}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="ec-report-intro">
            {report.intro}
          </p>
        </div>

        {/* Blocks */}
        <div className="space-y-4">
          {report.blocks.map((block, idx) => {
            const Icon = ICONS[block.id] || SunMedium;
            return (
              <div
                key={block.id}
                className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-border"
                data-testid={`ec-block-${block.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-primary/60">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <h3
                        className="text-lg font-medium text-primary leading-tight"
                        style={{ fontFamily: 'Outfit' }}
                        data-testid={`ec-block-${block.id}-title`}
                      >
                        {block.title}
                      </h3>
                    </div>
                    {block.summary && (
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {block.summary}
                      </p>
                    )}
                  </div>
                </div>

                {Array.isArray(block.accents) && block.accents.length > 0 && (
                  <ul
                    className="mt-3 space-y-2"
                    data-testid={`ec-block-${block.id}-accents`}
                  >
                    {block.accents.map((line, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-foreground/90 leading-relaxed bg-muted/30 rounded-md px-3 py-2 border border-border/60"
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/70 flex-shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Closing note */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          Powersure Energy Control Plan · gepersonaliseerd op basis van uw intake.
        </div>
      </div>
    </div>
  );
}
